import { lazy, Suspense, useMemo, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { ArrowDown, ArrowUp, CalendarDays, CheckCircle2, Circle, Gem, Lock, Pencil, Settings2, X } from 'lucide-react'
import { examAccentStyle } from '@/lib/examColors'
import { CheckMark } from '@/components/CheckMark'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { buttonVariants } from '@/components/ui/buttonVariants'
import { useConceptPopup } from '@/hooks/useConceptPopup'
import { StudyPlanConfigModal } from '@/components/StudyPlanConfigModal'
import { ExamHeatmap } from '@/components/ExamHeatmap'
import { ExamLogo } from '@/components/ExamLogo'
import { ProBadge } from '@/components/ProBadge'
import { StreakNavBadge } from '@/components/StreakBadge'
import { STREAK_ENABLED } from '@/lib/featureFlags'
import { QuizSessionCard } from '@/components/QuizSessionCard'
import { SessionCompletionOverlay } from '@/components/SessionCompletionOverlay'
import { OverlayPortal } from '@/components/ui/OverlayPortal'
import type { WikiExamSyllabus } from '@/lib/wikiParser'
import { wikiExamIdToProgressKey } from '@/lib/wikiParser'
import type { ConceptMasteryRecord, MasteryState } from '@/lib/mastery'
import { sanitizeMasteryState } from '@/lib/mastery'
import { LEVEL3_TEXT, LEVEL_FILL, masteryFill } from '@/lib/masteryFill'
import { MASTERY_LABEL } from '@/lib/masteryBadge'
import { computeExamReadiness } from '@/lib/readiness'
import { useReadinessDelta } from '@/hooks/useReadinessDelta'
import {
  RING_INNER_R,
  RING_OUTER_R,
  RING_VIEWBOX,
  RING_CX,
  RING_CY,
  buildRingSegments,
  ringArcPath,
  ringPolar,
  ringTopicGroups,
  type RingSegment,
} from '@/lib/readinessRing'
import type { WikiEntryRef } from '@/lib/wikiRoutes'
import { daysBetween, formatReadableDate, todayISO, type StudyPlan, type StudyPlanConfig } from '@/lib/studyPlan'
import {
  LOCALIZED_EXAMS,
  examWindowFor,
  formatSittingDate,
  getSittingsForExam,
  isValidSittingDate,
} from '@/data/examSittings'
import { readTodayLevelUps, LEVELUP_EVENT, type DailyLevelUp } from '@/lib/dailyProgressStore'
import {
  NEXT_STATE,
  buildDayPlanPct,
  buildTodayTargets,
  isConceptDoneToday,
} from '@/lib/planCompletion'
import { PLAN_LOCKED_EVENT, type PlanLockedDetail } from '@/lib/planForming'
import { useSchedulePlayback } from '@/hooks/useSchedulePlayback'
import type { QuizSession } from '@/lib/supabase'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { questionExamLabel } from '@/lib/examIds'
import { daysLeftLabel } from '@/lib/examMenu'
import { SegmentedControl } from '@/components/ui/SegmentedControl'

// The daily podcast's button and the episode it builds (docs/daily-podcast.md)
// are their own chunk: the card shouldn't wait on them.
const PodcastButton = lazy(() =>
  import('@/components/podcast/PodcastButton').then(m => ({ default: m.PodcastButton })),
)


// ── Study Guide Radial ─────────────────────────────────────────────────────────
//
// The readiness ring, drawn inside the **Study Guide** card below (the name
// here is the one the docs use for the ring itself — docs/exam-readiness.md).
// The arcs, their angles and the section groups come from `lib/readinessRing.ts`
// — the exam page's title-row ring draws the same shape from the same helpers.
// What lives here is only this surface's chrome: the legend, the curved section
// labels, and the middle — which carries the readiness score until a concept is
// hovered or selected, and that concept's readout while one is.

function StudyGuideRadial({
  syllabus,
  examRecords,
  now,
  pct,
  onConceptClick,
  selectedConcept,
  flashRadial,
}: {
  syllabus: WikiExamSyllabus
  examRecords: ConceptMasteryRecord[]
  now: Date
  /** The readiness score, already rounded — the card that owns the ring computes
   *  it once (`readinessPct`) so the KPI above and this number are one call. */
  pct: number
  onConceptClick?: (name: string) => void
  selectedConcept?: string | null
  flashRadial?: boolean
}) {
  const [hovered, setHovered] = useState<RingSegment | null>(null)

  const segments = useMemo(
    () => buildRingSegments(syllabus, examRecords, now),
    [syllabus, examRecords, now],
  )

  const selected = useMemo(
    () => selectedConcept ? (segments.find(s => s.conceptName.toLowerCase() === selectedConcept.toLowerCase()) ?? null) : null,
    [segments, selectedConcept],
  )

  const topicGroups = useMemo(() => ringTopicGroups(segments), [segments])

  const centerSeg = hovered ?? selected

  return (
    <div className="flex flex-col items-center gap-3">
      <svg
        viewBox={`0 0 ${RING_VIEWBOX} ${RING_VIEWBOX}`}
        className="w-full max-w-[260px]"
        style={{
          overflow: 'visible',
          // The flash `scrollToRadialTrigger` fires: the ring glows and the
          // score in its middle tints green with it.
          filter: flashRadial ? 'drop-shadow(0 0 10px rgba(34,197,94,0.55))' : 'none',
          transition: 'filter 0.5s ease-out',
        }}
      >
        <circle
          cx={RING_CX} cy={RING_CY}
          r={(RING_OUTER_R + RING_INNER_R) / 2}
          fill="none"
          strokeWidth={RING_OUTER_R - RING_INNER_R}
          stroke="rgba(34,197,94,0.06)"
        />
        {segments.map((seg, i) => {
          const isActive = hovered === seg || selected === seg
          return (
            <path
              key={i}
              d={ringArcPath(seg.startDeg, seg.endDeg, RING_OUTER_R, RING_INNER_R)}
              fill={masteryFill(seg.state)}
              opacity={(hovered || selected) && !isActive ? 0.35 : 1}
              stroke={selected === seg && hovered !== seg ? 'rgba(255,255,255,0.55)' : 'none'}
              strokeWidth={selected === seg && hovered !== seg ? 1 : 0}
              style={{
                transition: 'opacity 100ms, transform 100ms',
                transformOrigin: `${RING_CX}px ${RING_CY}px`,
                transform: isActive ? 'scale(1.04)' : 'scale(1)',
              }}
              onMouseEnter={() => setHovered(seg)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onConceptClick?.(seg.conceptName)}
              className="cursor-pointer"
            />
          )
        })}

        {/* Topic boundary dividers — all boundaries including i=0 (12 o'clock) */}
        {topicGroups.map((g, i) => {
          const p1 = ringPolar(g.boundaryDeg, RING_INNER_R - 4)
          const p2 = ringPolar(g.boundaryDeg, RING_OUTER_R + 4)
          return (
            <line
              key={`div-${i}`}
              x1={p1.x} y1={p1.y}
              x2={p2.x} y2={p2.y}
              stroke="currentColor"
              strokeWidth={1}
              opacity={0.22}
              strokeLinecap="round"
            />
          )
        })}

        {/* Curved topic labels — text follows the arc perimeter */}
        {topicGroups.map((g, i) => {
          const arcSpan = g.endDeg - g.startDeg
          if (arcSpan < 16) return null
          const LABEL_R = RING_OUTER_R + 9
          // Bottom half (90°–270°): reverse arc direction so text reads L→R
          const isBottom = g.midDeg > 90 && g.midDeg < 270
          const p1 = isBottom ? ringPolar(g.endDeg, LABEL_R) : ringPolar(g.startDeg, LABEL_R)
          const p2 = isBottom ? ringPolar(g.startDeg, LABEL_R) : ringPolar(g.endDeg, LABEL_R)
          const sweepFlag = isBottom ? 0 : 1
          const largeArcFlag = arcSpan > 180 ? 1 : 0
          const arcLen = (arcSpan / 360) * 2 * Math.PI * LABEL_R
          const maxChars = Math.max(2, Math.floor(arcLen / 4.2))
          const label = g.topicName.length > maxChars
            ? g.topicName.slice(0, maxChars - 1) + '…'
            : g.topicName
          const pathId = `topic-arc-${i}`
          return (
            <g key={`lbl-${i}`}>
              <defs>
                <path
                  id={pathId}
                  d={`M${p1.x} ${p1.y} A${LABEL_R} ${LABEL_R} 0 ${largeArcFlag} ${sweepFlag} ${p2.x} ${p2.y}`}
                />
              </defs>
              <text fontSize={7} fill="currentColor" opacity={0.65}>
                <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
                  {label}
                </textPath>
              </text>
            </g>
          )
        })}

        {centerSeg ? (
          <>
            <text x={RING_CX} y={RING_CY - 18} textAnchor="middle" fontSize={9} fill="currentColor" opacity={0.45} fontStyle="italic">
              {centerSeg.topicName.length > 24 ? centerSeg.topicName.slice(0, 22) + '…' : centerSeg.topicName}
            </text>
            <text x={RING_CX} y={RING_CY + 2} textAnchor="middle" fontSize={11} fill="currentColor" fontWeight="700">
              {centerSeg.conceptName.length > 20 ? centerSeg.conceptName.slice(0, 18) + '…' : centerSeg.conceptName}
            </text>
            <text
              x={RING_CX} y={RING_CY + 18} textAnchor="middle" fontSize={10}
              fill={centerSeg.state === 'level3' ? LEVEL3_TEXT : 'currentColor'}
              opacity={centerSeg.state === 'level3' ? 1 : 0.65}
            >
              {centerSeg.state === 'new' ? 'New' : centerSeg.state === 'level1' ? 'Level 1' : centerSeg.state === 'level2' ? 'Level 2' : centerSeg.state === 'level3' ? 'Level 3' : 'Forgotten'}
            </text>
          </>
        ) : (
          // The score itself, while nothing is hovered or selected. No caption
          // under it: the criterion beside the ring and the "Exam readiness"
          // KPI above both name what it measures, so a word here would be the
          // third time (docs/visual-noise-review.md, test 1).
          <text
            x={RING_CX} y={RING_CY + 14} textAnchor="middle" fontSize={40} fontWeight="800"
            fill={flashRadial ? '#22c55e' : 'currentColor'}
            style={{ transition: 'fill 0.8s ease-out' }}
          >
            {pct}%
          </text>
        )}
      </svg>

      {/* Legend — under the ring it reads, not above it. */}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        {(['level3', 'level2', 'level1', 'new'] as MasteryState[]).map(s => (
          <span key={s} className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: LEVEL_FILL[s] }}
            />
            {s === 'new' ? 'New' : s === 'level1' ? 'Level 1' : s === 'level2' ? 'Level 2' : 'Level 3'}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Mini readiness ring ────────────────────────────────────────────────────────
//
// The dashboard card's ring: the Study Guide ring's arcs drawn thin around the
// score, with nothing else — no labels, no legend, no hover. The full ring is a
// tap away in the popup it opens.

const MINI_OUTER_R = RING_OUTER_R + 8
const MINI_INNER_R = RING_OUTER_R - 14

function MiniReadinessRing({ segments, pct, flash }: { segments: RingSegment[]; pct: number; flash?: boolean }) {
  return (
    <svg
      viewBox={`0 0 ${RING_VIEWBOX} ${RING_VIEWBOX}`}
      className="h-full w-full"
      aria-hidden="true"
      style={{
        filter: flash ? 'drop-shadow(0 0 8px rgba(34,197,94,0.55))' : 'none',
        transition: 'filter 0.5s ease-out',
      }}
    >
      <circle
        cx={RING_CX} cy={RING_CY}
        r={(MINI_OUTER_R + MINI_INNER_R) / 2}
        fill="none"
        strokeWidth={MINI_OUTER_R - MINI_INNER_R}
        stroke="rgba(34,197,94,0.06)"
      />
      {segments.map((seg, i) => (
        <path key={i} d={ringArcPath(seg.startDeg, seg.endDeg, MINI_OUTER_R, MINI_INNER_R)} fill={masteryFill(seg.state)} />
      ))}
      <text
        x={RING_CX} y={RING_CY + 12} textAnchor="middle" fontSize={68} fontWeight="800"
        fill={flash ? '#22c55e' : 'currentColor'}
        style={{ transition: 'fill 0.8s ease-out' }}
      >
        {pct}%
      </text>
      <text x={RING_CX} y={RING_CY + 52} textAnchor="middle" fontSize={30} fill="currentColor" opacity={0.6}>
        Ready
      </text>
    </svg>
  )
}

// ── Study-plan sub-components ──────────────────────────────────────────────────

function ReviewModeNote({ concepts }: { concepts: string[] }) {
  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30 px-3 py-2.5 text-xs text-purple-800 dark:text-purple-300 space-y-1">
      <div className="flex items-center gap-1.5 font-medium">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
        All concepts mastered. Great work!
      </div>
      <p>Today's plan uses spaced repetition to keep your weakest concepts fresh.</p>
      {concepts.length > 0 && (
        <p>Reviewing: {concepts.slice(0, 3).join(', ')}{concepts.length > 3 ? ` +${concepts.length - 3} more` : ''}</p>
      )}
    </div>
  )
}

// ── ReadinessCard ──────────────────────────────────────────────────────────────

interface Props {
  syllabus: WikiExamSyllabus
  masteryRecords: ConceptMasteryRecord[]
  sessions: QuizSession[]
  plan: StudyPlan | null
  masteryStateByName: Map<string, MasteryState>
  config: StudyPlanConfig
  loading: boolean
  /** Whether `masteryRecords` is still being fetched. The readiness score is derived from
   *  those records, so until they land it reads near zero — which is fine to print (the
   *  card is empty anyway) but must not become the day's baseline for the movement arrow
   *  beside it, or the records arriving would look like a jump the learner earned. */
  masteryLoading?: boolean
  examDate: string | null
  onConfigChange: (next: Partial<StudyPlanConfig>) => void
  onRegenerate: () => void
  onReplaceConcepts?: (concepts: string[]) => void
  onExamDateChange?: (date: string | null) => void
  onOpenOnboarding?: (step?: 1 | 2 | 3) => void
  openConceptsTrigger?: number
  startQuizTrigger?: number
  scrollToRadialTrigger?: number
  /** Whether the user has access to the custom Study Plan. Defaults to true. */
  isPro?: boolean
  /** Reports whether today's study plan is fully complete, so the Dashboard's readiness stat can show a checkmark. */
  onPlanCompletionChange?: (complete: boolean) => void
  /** Bumped by the Dashboard (e.g. tapping the readiness-stat checkmark) to open the day-complete/bonus info panel. */
  openDayCompleteInfoTrigger?: number
  /** DOM node the Exam readiness card portals into — the Dashboard puts it directly
   *  under the exam tabs so readiness is the first thing on the page. Renders inline
   *  at the top of this card's own stack when omitted. */
  readinessSlot?: HTMLElement | null
  /** The primary actions that act on this score (Read concepts / Fix mistakes /
   *  Start quiz). Rendered straight under the Exam readiness card, bare on the
   *  page — but authored by the Dashboard, which
   *  owns their triggers. Omitted, the group ends with the card. */
  actions?: ReactNode
}

type ScheduleView = 'week' | 'overview'
const SCHEDULE_VIEW_KEY = 'actuarial_schedule_view'

export function ReadinessCard({
  syllabus, masteryRecords, sessions, plan, masteryStateByName,
  config, loading, masteryLoading = false, examDate, onConfigChange, onRegenerate, onReplaceConcepts, onExamDateChange,
  openConceptsTrigger, startQuizTrigger, scrollToRadialTrigger,
  isPro = true, onPlanCompletionChange, openDayCompleteInfoTrigger,
  readinessSlot, actions,
}: Props) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const openDashboard = useConceptPopup(s => s.openDashboard)
  const popupOpen = useConceptPopup(s => s.open)
  const popupCurrentName = useConceptPopup(s => s.open ? (s.list[s.index]?.name ?? null) : null)
  const popupFromRadial = useConceptPopup(s => s.dashboardContext?.fromRadial ?? false)
  const popupDashboardFilter = useConceptPopup(s => s.dashboardContext?.filter ?? 'entire-syllabus')
  const toRefs = (arr: { name: string }[]): WikiEntryRef[] =>
    arr.map(c => ({ kind: 'concept' as const, name: c.name }))
  const prevPopupConceptRef = useRef<string | null>(null)
  const studyGuideCardRef = useRef<HTMLDivElement>(null)
  const readinessSquareRef = useRef<HTMLButtonElement>(null)
  // The Study Guide popup the readiness square opens.
  const [ringOpen, setRingOpen] = useState(false)
  const ringOpenRef = useRef(false)
  ringOpenRef.current = ringOpen
  const studyPlanCardRef = useRef<HTMLDivElement>(null)
  const [flashingConcept, setFlashingConcept] = useState<string | null>(null)
  const [flashRadial, setFlashRadial] = useState(false)
  const [quizStartConcept, setQuizStartConcept] = useState<string | null>(null)
  const [tracePlanBorder, setTracePlanBorder] = useState(false)
  const isLaunchingQuizRef = useRef(false)
  const [recentlyCompletedConcepts, setRecentlyCompletedConcepts] = useState<Set<string>>(new Set())
  const prevCompletedRef = useRef<Set<string>>(new Set())
  const [completedToday, setCompletedToday] = useState<DailyLevelUp[]>([])
  const [showConfig, setShowConfig] = useState(false)
  const [configInitialStep, setConfigInitialStep] = useState<1 | 2 | 3>(1)
  const [showDayCompleteInfo, setShowDayCompleteInfo] = useState(false)
  const [bonusClaimed, setBonusClaimed] = useState<boolean>(() => {
    try {
      const key = `actuarial_daily_bonus_${wikiExamIdToProgressKey(syllabus.examId)}_${todayISO()}`
      const raw = localStorage.getItem(key)
      return raw ? !!(JSON.parse(raw) as { amount?: number }).amount : false
    } catch { return false }
  })
  const [claimedBonusAmount, setClaimedBonusAmount] = useState<number>(() => {
    try {
      const key = `actuarial_daily_bonus_${wikiExamIdToProgressKey(syllabus.examId)}_${todayISO()}`
      const raw = localStorage.getItem(key)
      return raw ? ((JSON.parse(raw) as { amount?: number }).amount ?? 0) : 0
    } catch { return 0 }
  })
  const [highlightedTopicIdx, setHighlightedTopicIdx] = useState<number | null>(null)

  // Quiz history state
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [selectedDayLevelUps, setSelectedDayLevelUps] = useState<DailyLevelUp[]>([])
  const [viewingSession, setViewingSession] = useState<QuizSession | null>(null)
  const [allDailyCompletions, setAllDailyCompletions] = useState<Array<{ day: string; concept_slug: string }>>([])
  const [allDailyCompletionsVersion, setAllDailyCompletionsVersion] = useState(0)
  const savedScrollY = useRef(0)

  // "Schedule forming": after a plan is locked in, this card rewinds through
  // the new schedule — the strip sweeps from exam day back to today and the day
  // panel below shows each day's concepts as it passes.
  const playback = useSchedulePlayback()
  // The strip's view — a week across the card, or the whole run to the exam —
  // remembered per browser. A week is the default: it is where today's work is.
  const [scheduleView, setScheduleView] = useState<ScheduleView>(() => {
    try { return localStorage.getItem(SCHEDULE_VIEW_KEY) === 'overview' ? 'overview' : 'week' } catch { return 'week' }
  })
  const changeScheduleView = (view: ScheduleView) => {
    setScheduleView(view)
    try { localStorage.setItem(SCHEDULE_VIEW_KEY, view) } catch { /* private window */ }
  }
  const studyScheduleCardRef = useRef<HTMLDivElement>(null)
  const planRef = useRef(plan)
  useEffect(() => { planRef.current = plan }, [plan])

  useEffect(() => {
    if (openConceptsTrigger && allConcepts.length > 0) {
      const hasStudyPlan = studyPlanConceptsForModal.length > 0
      openDashboard(
        toRefs(allConcepts),
        hasStudyPlan ? toRefs(studyPlanConceptsForModal) : null,
        hasStudyPlan ? 'study-plan' : 'entire-syllabus',
        0,
      )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConceptsTrigger])

  useEffect(() => {
    const handleSessionSaved = () => setAllDailyCompletionsVersion(v => v + 1)
    window.addEventListener('quiz-session-saved', handleSessionSaved)
    return () => window.removeEventListener('quiz-session-saved', handleSessionSaved)
  }, [])

  useEffect(() => {
    setCompletedToday(readTodayLevelUps())
    function handleLevelUp(e: Event) {
      setCompletedToday((e as CustomEvent<DailyLevelUp[]>).detail)
    }
    function handleStorage() {
      setCompletedToday(readTodayLevelUps())
    }
    window.addEventListener(LEVELUP_EVENT, handleLevelUp)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(LEVELUP_EVENT, handleLevelUp)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  // Flash a concept row green when it transitions to completed for the first time today.
  const markConceptComplete = useCallback((slug: string) => {
    setRecentlyCompletedConcepts(prev => new Set([...prev, slug]))
    setTimeout(() => {
      setRecentlyCompletedConcepts(prev => {
        const next = new Set(prev); next.delete(slug); return next
      })
    }, 800)
  }, [])

  useEffect(() => {
    const nowCompleted = new Set(
      completedToday.map(lu => lu.conceptSlug.toLowerCase())
    )
    for (const slug of nowCompleted) {
      if (!prevCompletedRef.current.has(slug)) markConceptComplete(slug)
    }
    prevCompletedRef.current = nowCompleted
  }, [completedToday, markConceptComplete])

  const now = useMemo(() => new Date(), [])
  const progressKey = wikiExamIdToProgressKey(syllabus.examId)
  const examLabel = questionExamLabel(syllabus)

  // The day the strip highlights and the panel below describes: the sweep while
  // it's running, otherwise whatever day the user clicked. Deriving it (rather
  // than mirroring the sweep into `selectedDay`) keeps the two in lockstep and
  // keeps the sweep from firing a level-up query for every day it passes.
  const displayDay = playback.day ?? selectedDay

  // Locking in a plan (from any of the places the config modal opens) scrolls
  // this card into view and plays the new schedule forming on it. The plan
  // regenerates in the owner's effect a render or two after the save, so the
  // sweep waits a beat and then reads whatever plan has landed.
  useEffect(() => {
    if (!isPro) return
    function handlePlanLocked(e: Event) {
      const detail = (e as CustomEvent<PlanLockedDetail>).detail
      if (detail?.examId !== progressKey) return
      studyScheduleCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.setTimeout(() => {
        const fresh = planRef.current
        if (fresh) playback.start(fresh, examDate)
      }, 260)
    }
    window.addEventListener(PLAN_LOCKED_EVENT, handlePlanLocked)
    return () => window.removeEventListener(PLAN_LOCKED_EVENT, handlePlanLocked)
  }, [progressKey, examDate, isPro, playback])


  // Fetch all daily_completions for this exam — used for heatmap plan-completion coloring
  useEffect(() => {
    if (!user) { setAllDailyCompletions([]); return }
    let cancelled = false
    supabase
      .from('daily_completions')
      .select('day, concept_slug')
      .eq('user_id', user.id)
      .eq('exam_id', progressKey)
      .then(({ data }: { data: Array<{ day: string; concept_slug: string }> | null }) => {
        if (!cancelled) setAllDailyCompletions(data ?? [])
      })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, progressKey, allDailyCompletionsVersion])

  useEffect(() => {
    const today = todayISO()
    if (!selectedDay || selectedDay === today) { setSelectedDayLevelUps([]); return }
    if (!user) { setSelectedDayLevelUps([]); return }
    let cancelled = false
    supabase
      .from('daily_completions')
      .select('*')
      .eq('user_id', user.id)
      .eq('day', selectedDay)
      .eq('exam_id', progressKey)
      .then(({ data }: { data: Array<{ concept_slug: string; from_state: string; to_state: string; at: string }> | null }) => {
        if (cancelled) return
        setSelectedDayLevelUps((data ?? []).map(r => ({
          conceptSlug: r.concept_slug,
          from: sanitizeMasteryState(r.from_state),
          to: sanitizeMasteryState(r.to_state),
          at: r.at,
        })))
      })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, user, progressKey])

  // Sessions for this exam. `quiz_sessions.exam` carries the question bank's
  // label ("Exam 5"), not the syllabus subject line, so the match goes through
  // questionExamLabel — see lib/examIds.
  const examSessions = useMemo(
    () => sessions.filter(s => s.exam === examLabel),
    [sessions, examLabel],
  )

  const examRecords = useMemo(
    () => masteryRecords.filter(r => r.exam_id === progressKey),
    [masteryRecords, progressKey],
  )

  // The arcs the readiness square's mini ring draws — the same segments as the
  // Study Guide ring in the popup, so the two can't disagree.
  const ringSegments = useMemo(
    () => buildRingSegments(syllabus, examRecords, now),
    [syllabus, examRecords, now],
  )

  // Esc closes the Study Guide popup — unless a concept is open over it, in
  // which case that Esc belongs to the concept popup (one layer at a time).
  useEffect(() => {
    if (!ringOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !useConceptPopup.getState().open) setRingOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [ringOpen])

  // The one readiness score (docs/exam-readiness.md): syllabus coverage.
  // Computed once here and read by both halves of the card below — the ring
  // draws `overallPct`, the row beside it draws the criterion it is made of —
  // so the two can never quote different numbers.
  //
  // Scored from real mastery on every tier. Mastery is recorded for every
  // account — the daily study *plan* is what Pro buys, not the record of
  // what has been learned — so scoring a free account against an empty record
  // list reported 0% readiness to someone who had just levelled concepts up,
  // which reads as a broken app rather than as a locked feature.
  const readiness = useMemo(
    () => computeExamReadiness(syllabus, examRecords, now),
    [syllabus, examRecords, now],
  )

  // How far that score has moved today, in whole points — the arrow beside the number
  // (lib/readinessDelta.ts). Held back with a null while the mastery records are still
  // arriving, so an empty record set never becomes the day's starting line.
  const readinessMoveToday = useReadinessDelta(
    user?.id ?? null,
    wikiExamIdToProgressKey(syllabus.examId),
    !masteryLoading && readiness.counts.total > 0 ? readiness.overallPct : null,
  )

  const allConcepts = useMemo(
    () => syllabus.topics.flatMap(t => t.concepts.map(c => ({
      name: c.name,
      state: (examRecords.find(r =>
        r.concept_slug.toLowerCase() === c.name.toLowerCase() ||
        r.concept_slug.toLowerCase() === c.target.toLowerCase()
      )?.state ?? 'new') as MasteryState,
    }))),
    [syllabus, examRecords],
  )

  // Set of concept names belonging to this exam — used to scope today's level-ups
  // so that completing an Exam P concept never shows up in an Exam FM plan.
  const syllabusConceptNames = useMemo(() =>
    new Set(syllabus.topics.flatMap(t => t.concepts.map(c => c.name.toLowerCase()))),
    [syllabus]
  )

  // Today's level-ups scoped to this exam's syllabus only
  const examCompletedToday = useMemo(() =>
    completedToday.filter(lu => syllabusConceptNames.has(lu.conceptSlug.toLowerCase())),
    [completedToday, syllabusConceptNames]
  )

  // Keep the today panel in sync with live level-up events. Scoped to this
  // exam's concepts, like every other count in the panel — a level-up earned on
  // another exam belongs on that exam's schedule, not this one's.
  useEffect(() => {
    if (selectedDay === todayISO()) setSelectedDayLevelUps(examCompletedToday)
  }, [selectedDay, examCompletedToday])

  const displayConcepts = useMemo(
    () => plan?.status === 'review_mode'
      ? (plan?.reviewConcepts ?? [])
      : (plan?.todaysConcepts ?? []),
    [plan],
  )

  // Group today's plan concepts by their parent syllabus topic
  const groupedPlanConcepts = useMemo(() => {
    if (displayConcepts.length === 0) return []
    const dcSet = new Set(displayConcepts.map(c => c.toLowerCase()))
    return syllabus.topics
      .map(t => ({
        topicName: t.name,
        concepts: t.concepts.filter(c => dcSet.has(c.name.toLowerCase())).map(c => c.name),
      }))
      .filter(g => g.concepts.length > 0)
  }, [syllabus, displayConcepts])

  // Flattened (concept, owning-group-index) pairs in display order — drives the
  // "Start Today's Quiz" cascade so each concept row lights up individually while
  // its parent topic group stays subtly highlighted.
  const cascadeSteps = useMemo(
    () => groupedPlanConcepts.flatMap((g, groupIdx) => g.concepts.map(name => ({ name, groupIdx }))),
    [groupedPlanConcepts]
  )

  // Per-concept targets for today — shared with the Dashboard's Today card and
  // the study-guide header's plan list (see lib/planCompletion).
  const targetByName = useMemo(
    () => buildTodayTargets(plan?.assignments ?? [], masteryStateByName, todayISO()),
    [plan, masteryStateByName]
  )

  // Order matches the visual display order (syllabus topic order) so that
  // prev/next in the popup navigates in the same order the user sees on screen.
  const studyPlanConceptsForModal = useMemo(() =>
    groupedPlanConcepts.flatMap(g => g.concepts.map(name => ({
      name,
      state: masteryStateByName.get(name.toLowerCase()) ?? 'new' as MasteryState,
    }))),
    [groupedPlanConcepts, masteryStateByName]
  )

  const todayStr = todayISO()

  const todayQuestionsAnswered = useMemo(() =>
    examSessions
      .filter(s => s.completed_at.slice(0, 10) === todayStr)
      .reduce((sum, s) => sum + s.total_questions, 0),
    [examSessions, todayStr]
  )

  const todayGemsEarned = useMemo(() =>
    examSessions
      .filter(s => s.completed_at.slice(0, 10) === todayStr)
      .reduce((sum, s) => sum + s.correct_count, 0),
    [examSessions, todayStr]
  )

  const todayLevelUps = examCompletedToday.length

  const allConceptsDone = useMemo(() =>
    displayConcepts.length > 0 && displayConcepts.every(name =>
      isConceptDoneToday(name, targetByName, masteryStateByName, examCompletedToday)
    ),
    [displayConcepts, examCompletedToday, targetByName, masteryStateByName]
  )

  // Concepts levelled up today for this exam that weren't in the original plan
  const bonusConcepts = useMemo(() =>
    examCompletedToday.filter(lu =>
      !displayConcepts.some(n => n.toLowerCase() === lu.conceptSlug.toLowerCase())
    ),
    [examCompletedToday, displayConcepts]
  )

  // Originally planned concepts that are not yet completed (for this exam)
  const incompleteOriginalConcepts = useMemo(() =>
    displayConcepts.filter(name =>
      !isConceptDoneToday(name, targetByName, masteryStateByName, examCompletedToday)
    ),
    [displayConcepts, examCompletedToday, targetByName, masteryStateByName]
  )

  const showReplaceButton = bonusConcepts.length > 0 && incompleteOriginalConcepts.length > 0

  // Per-day plan completion % — the heatmap's green. Today is scored against
  // today's plan with the same rule that ticks the checklist off, so a day the
  // user has finished is a fully bright cell; past days fall back to the pace.
  // With no plan this stays undefined and ExamHeatmap colours by session score.
  // See buildDayPlanPct in lib/planCompletion.
  const dayPlanPct = useMemo((): Map<string, number> | undefined => {
    if (!user || !plan) return undefined
    const today = todayISO()

    // Per-day slug sets from daily_completions, plus today's live level-ups
    // (which land here before the row is read back).
    const completionsByDay = new Map<string, Set<string>>()
    for (const row of allDailyCompletions) {
      if (!completionsByDay.has(row.day)) completionsByDay.set(row.day, new Set())
      completionsByDay.get(row.day)!.add(row.concept_slug.toLowerCase())
    }
    if (examCompletedToday.length > 0) {
      if (!completionsByDay.has(today)) completionsByDay.set(today, new Set())
      const todaySet = completionsByDay.get(today)!
      for (const lu of examCompletedToday) todaySet.add(lu.conceptSlug.toLowerCase())
    }

    return buildDayPlanPct({
      today,
      completionsByDay,
      todaysConcepts: displayConcepts,
      targets: targetByName,
      masteryStateByName,
      levelUps: examCompletedToday,
      conceptsPerDay: plan.conceptsPerDay,
      studiedToday: todayQuestionsAnswered > 0,
    })
  }, [user, plan, allDailyCompletions, examCompletedToday, displayConcepts, masteryStateByName, targetByName, todayQuestionsAnswered])

  function handleReplace() {
    // One-for-one swap: each bonus concept replaces exactly one incomplete planned concept.
    // Completed originals stay. Incomplete originals beyond the bonus count also stay.
    const completedOriginal = displayConcepts.filter(name =>
      !incompleteOriginalConcepts.some(n => n.toLowerCase() === name.toLowerCase())
    )
    const bonusSlugs = bonusConcepts.map(lu => lu.conceptSlug)
    // Incomplete concepts not covered by a bonus slot remain in the plan
    const remainingIncomplete = incompleteOriginalConcepts.slice(bonusSlugs.length)
    const merged = [...completedOriginal, ...bonusSlugs, ...remainingIncomplete]
    // Deduplicate while preserving order
    const seen = new Set<string>()
    const newConcepts = merged.filter(n => {
      const k = n.toLowerCase()
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    onReplaceConcepts?.(newConcepts)
  }

  // Auto-claim the 2× daily bonus when all plan concepts are done
  useEffect(() => {
    if (!allConceptsDone || bonusClaimed || todayGemsEarned === 0 || !user) return
    const amount = todayGemsEarned
    supabase.rpc('award_gems', { p_amount: amount })
      .then(({ error }: { error: { message: string } | null }) => {
        if (!error) {
          setBonusClaimed(true)
          setClaimedBonusAmount(amount)
          localStorage.setItem(
            `actuarial_daily_bonus_${progressKey}_${todayISO()}`,
            JSON.stringify({ amount })
          )
          window.dispatchEvent(new CustomEvent('gems-awarded', { detail: { amount } }))
        }
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allConceptsDone, bonusClaimed, todayGemsEarned, user, progressKey])

  // Report completion status up so the Dashboard can mark the readiness stat done
  useEffect(() => {
    onPlanCompletionChange?.(isPro && displayConcepts.length > 0 && allConceptsDone)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, displayConcepts.length, allConceptsDone])

  // Open the day-complete/bonus info panel when the Dashboard's readiness-stat checkmark is tapped
  useEffect(() => {
    if (openDayCompleteInfoTrigger) setShowDayCompleteInfo(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDayCompleteInfoTrigger])

  // Only the two fields the label reads, so the callback below doesn't change
  // identity whenever the syllabus object does.
  const { examId: syllabusExamId, examTopic: syllabusExamTopic } = syllabus
  const quizTopicValue = useMemo(
    () => questionExamLabel({ examId: syllabusExamId, examTopic: syllabusExamTopic }),
    [syllabusExamId, syllabusExamTopic],
  )

  const handleStartQuiz = useCallback(() => {
    if (isLaunchingQuizRef.current) return
    isLaunchingQuizRef.current = true

    const topicValue = quizTopicValue

    // No daily plan to sweep through — a free account, or a Pro one that
    // hasn't set a target date. The cascade is an animation *of the plan*, and
    // `autostart` needs one, so both would be a second of nothing before the
    // quiz builder appeared anyway. Go straight there, with the exam already
    // chosen.
    if (cascadeSteps.length === 0) {
      isLaunchingQuizRef.current = false
      navigate(`/?topic=${encodeURIComponent(topicValue)}&mode=quiz`)
      return
    }

    // Scroll the study plan into view first, then sweep a highlight through each
    // concept individually while the card border is traced in the primary colour —
    // together they tell the user exactly what today's session is about to cover.
    studyPlanCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTracePlanBorder(true)

    const n = cascadeSteps.length
    const startDelay = 150
    // Keep the whole cascade snappy even for long plans by shrinking the per-concept step.
    const step = n > 0 ? Math.max(70, Math.min(130, 1000 / n)) : 0
    cascadeSteps.forEach((s, idx) => {
      setTimeout(() => {
        setHighlightedTopicIdx(s.groupIdx)
        setQuizStartConcept(s.name.toLowerCase())
      }, startDelay + idx * step)
    })

    const cascadeEnd = startDelay + n * step
    const totalDelay = Math.max(cascadeEnd + 400, 900)
    setTimeout(() => {
      setHighlightedTopicIdx(null)
      setQuizStartConcept(null)
      setTracePlanBorder(false)
      isLaunchingQuizRef.current = false
      // autostart=1 tells Landing to jump straight into a quiz sized to complete
      // today's plan (fewest questions covering every still-incomplete concept).
      navigate(`/?topic=${encodeURIComponent(topicValue)}&mode=quiz&autostart=1`)
    }, totalDelay)
  }, [navigate, quizTopicValue, cascadeSteps])

  useEffect(() => {
    if (startQuizTrigger) handleStartQuiz()
  }, [startQuizTrigger, handleStartQuiz])

  useEffect(() => {
    if (!scrollToRadialTrigger) return
    readinessSquareRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setFlashRadial(true)
    const t = setTimeout(() => setFlashRadial(false), 1200)
    return () => clearTimeout(t)
  }, [scrollToRadialTrigger])

  useEffect(() => {
    if (!popupOpen) {
      prevPopupConceptRef.current = null
    }
  }, [popupOpen])

  useEffect(() => {
    if (!popupCurrentName || popupCurrentName === prevPopupConceptRef.current) return
    prevPopupConceptRef.current = popupCurrentName
    const fromRadial = popupFromRadial

    if (fromRadial) {
      // The ring lives in the Study Guide popup, which ends where the concept
      // popup begins — it is always in view, so there is nothing to scroll.
      if (ringOpenRef.current) return
      // On every concept change, re-check whether the study guide card is still
      // visible above the popup. If it has scrolled out of view, bring it back.
      // The effect cleanup cancels a pending timeout on re-run, so rapid Next/Prev
      // clicks naturally debounce down to a single scroll at the end.
      const scrollId = setTimeout(() => {
        if (!studyGuideCardRef.current) return
        const popupEl = document.querySelector<HTMLElement>('.concept-popup-aside')
        const visibleHeight = popupEl ? popupEl.getBoundingClientRect().top : window.innerHeight
        const rect = studyGuideCardRef.current.getBoundingClientRect()
        if (rect.top < 0 || rect.top > visibleHeight - 80) {
          window.scrollBy({ top: rect.top - 16, behavior: 'smooth' })
        }
      }, 50)
      return () => clearTimeout(scrollId)
    }

    // Non-radial: scroll to the concept's row in the study plan card. Only the
    // plan lists concept rows now — the Topics Learned list the entire-syllabus
    // branch used to reveal is gone, and the ring above is what that mode is
    // read against.
    let scrollId: ReturnType<typeof setTimeout> | undefined
    if (popupDashboardFilter === 'study-plan') {
      scrollId = setTimeout(() => {
        const popupEl = document.querySelector<HTMLElement>('.concept-popup-aside')
        const visibleHeight = popupEl ? popupEl.getBoundingClientRect().top : window.innerHeight
        const el = document.querySelector<HTMLElement>(`[data-study-concept="${CSS.escape(popupCurrentName.toLowerCase())}"]`)
        if (!el) return
        const rect = el.getBoundingClientRect()
        window.scrollBy({ top: rect.top - visibleHeight / 2 + rect.height / 2, behavior: 'smooth' })
      }, 50)
    }
    setFlashingConcept(popupCurrentName)
    const clearId = setTimeout(() => setFlashingConcept(null), 1400)
    return () => { if (scrollId !== undefined) clearTimeout(scrollId); clearTimeout(clearId) }
  }, [popupCurrentName, popupFromRadial, popupDashboardFilter])

  // Today's Study Plan — the Dashboard's second card, directly under the
  // readiness score: the number says how ready you are, this says what to do
  // about it today. It rides inside `readinessCardContent` below, between the
  // score and the ring, so it portals into the Dashboard's slot with them.
  const studyPlanCardContent = (
    <>
      {/* Pro: today's concepts. Stays visible regardless of which day is
          selected on the Study Schedule heatmap. */}
      {isPro && displayConcepts.length > 0 && (
        <Card
          ref={studyPlanCardRef}
          className={`border-0 relative transition-colors ${allConceptsDone ? 'bg-green-500/10 dark:bg-green-500/15' : ''}`}
        >
          {allConceptsDone && (
            <button
              type="button"
              onClick={() => setShowDayCompleteInfo(true)}
              className="absolute -top-2 -right-2 z-10 inline-flex rounded-full text-green-500 shadow ring-2 ring-background transition-colors hover:text-green-600"
              aria-label="Today's study plan complete — view bonus details"
              title="Today's study plan complete — tap for details"
            >
              <CheckMark className="block h-7 w-7 text-current" />
            </button>
          )}
          {tracePlanBorder && (
            <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
              {/* Rainbow foil ramp — the same sky→fuchsia→gold the collected-card
                  and lock borders wear, run diagonally across the card so the
                  traced edge picks up all three. */}
              <defs>
                <linearGradient id="study-plan-trace-foil" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="rgb(56 189 248)" />
                  <stop offset="35%" stopColor="rgb(232 121 249)" />
                  <stop offset="70%" stopColor="rgb(250 204 21)" />
                  <stop offset="100%" stopColor="rgb(56 189 248)" />
                </linearGradient>
              </defs>
              <rect
                x={0} y={0} width="100%" height="100%"
                rx={8} ry={8}
                fill="none"
                stroke="url(#study-plan-trace-foil)"
                strokeWidth={2}
                pathLength={100}
                className="study-plan-trace-path"
              />
            </svg>
          )}
          <CardContent className="p-6">
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">Today's Study Plan</h3>
                <ProBadge />
                {/* The study streak — renders nothing without a live streak. */}
                {STREAK_ENABLED && (
                  <span className="ml-auto shrink-0"><StreakNavBadge size="md" /></span>
                )}
              </div>

              {/* The daily podcast — today's plan, read aloud. Nothing until
                  an episode exists (docs/daily-podcast.md). */}
              <Suspense fallback={null}>
                <PodcastButton plan={plan} examKey={progressKey} examLabel={syllabus.examLabel} />
              </Suspense>

              {/* Progress pills — shown when there's activity today */}
              {todayQuestionsAnswered > 0 && !allConceptsDone && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2.5 py-1 text-sm font-medium">
                    <CheckMark className="h-3.5 w-3.5" />
                    {todayGemsEarned}/{todayQuestionsAnswered} correct
                  </span>
                  {todayGemsEarned > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2.5 py-1 text-sm font-medium">
                      <Gem className="h-3 w-3 text-cyan-400" />
                      {todayGemsEarned} gems
                    </span>
                  )}
                  {todayLevelUps > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2.5 py-1 text-sm font-medium">
                      <ArrowUp className="h-3 w-3 text-primary" />
                      {todayLevelUps} levelled up
                    </span>
                  )}
                </div>
              )}

              {/* Grouped concept list — topic heading with its concepts listed directly below */}
              <div className="space-y-4">
                {groupedPlanConcepts.map((group, groupIdx) => {
                  const isHighlighted = highlightedTopicIdx === groupIdx
                  return (
                    <div
                      key={group.topicName}
                      className={`space-y-0.5 rounded-lg px-2 py-1.5 -mx-2 transition-colors duration-200 ${isHighlighted ? 'plan-foil-ring bg-gradient-to-br from-sky-400/10 via-fuchsia-400/10 to-amber-300/10' : ''}`}
                    >
                      {/* Topic heading */}
                      <p className="text-sm font-semibold text-muted-foreground truncate">
                        {group.topicName}
                      </p>
                      {/* Concepts */}
                      {group.concepts.map(name => {
                        const target = targetByName.get(name.toLowerCase()) ?? 'level1'
                        const isCompleted = isConceptDoneToday(name, targetByName, masteryStateByName, examCompletedToday)
                        const planIdx = studyPlanConceptsForModal.findIndex(c => c.name.toLowerCase() === name.toLowerCase())
                        const isCascadeHighlighted = quizStartConcept === name.toLowerCase()
                        return (
                          <div key={name} className={`flex items-center gap-2 w-full${isCascadeHighlighted ? ' concept-row-foil' : (flashingConcept?.toLowerCase() === name.toLowerCase() ? ' concept-row-highlight' : '')}${recentlyCompletedConcepts.has(name.toLowerCase()) ? ' concept-success' : ''}`}>
                            {isCompleted
                              ? <CheckMark className="h-5 w-5" />
                              : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
                            {/* Concept name — opens popup */}
                            <button
                              type="button"
                              data-study-concept={name.toLowerCase()}
                              onClick={() => openDashboard(toRefs(allConcepts), toRefs(studyPlanConceptsForModal), 'study-plan', planIdx === -1 ? 0 : planIdx)}
                              className={`flex-1 text-left text-base py-1.5 truncate transition-colors hover:text-foreground/80 ${isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'}`}
                            >
                              {name}
                            </button>
                            <span className={`text-sm shrink-0 ${isCompleted ? 'text-green-600 dark:text-green-400 font-medium' : 'text-muted-foreground'}`}>→ {MASTERY_LABEL[target]}</span>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}

                {/* Bonus concepts */}
                {bonusConcepts.length > 0 && (
                  // One group, spaced like a topic's rows — loose in the
                  // list above, each row took the list's gap between topics.
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2 pt-1 pb-1">
                      <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
                      <span className="text-sm text-muted-foreground shrink-0">Also completed today</span>
                      <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
                    </div>
                    {bonusConcepts.map(lu => {
                      const globalIdx = allConcepts.findIndex(ac => ac.name.toLowerCase() === lu.conceptSlug.toLowerCase())
                      return (
                        <button
                          key={lu.conceptSlug}
                          type="button"
                          data-study-concept={lu.conceptSlug.toLowerCase()}
                          onClick={() => openDashboard(toRefs(allConcepts), toRefs(studyPlanConceptsForModal), 'entire-syllabus', globalIdx === -1 ? 0 : globalIdx)}
                          className={`w-full flex items-center gap-2 text-left transition-colors${flashingConcept?.toLowerCase() === lu.conceptSlug.toLowerCase() ? ' concept-row-highlight' : ''}`}
                        >
                          <CheckMark className="h-5 w-5" />
                          <span className="text-base py-1.5 flex-1 min-w-0 truncate text-muted-foreground line-through hover:text-foreground/80">
                            {lu.conceptSlug}
                          </span>
                          <span className="text-sm text-green-600 dark:text-green-400 shrink-0 font-medium">
                            → {MASTERY_LABEL[lu.to]}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Replace button */}
              {showReplaceButton && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReplace}
                  className="w-full gap-1.5 text-xs h-8"
                >
                  Replace with today's completed concepts
                </Button>
              )}

            </div>
          </CardContent>
        </Card>
      )}

      {/* Locked study plan — non-Pro only, standing in the same place as
          the Pro Today's Study Plan card. */}
      {!isPro && (
        <div className="relative rounded-xl overflow-hidden">
          {/* Blurred background: Custom Study Plan card */}
          <div className="absolute inset-0 pointer-events-none select-none blur-sm opacity-50 rounded-xl" aria-hidden="true">
            <div className="h-full bg-card rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Today's Study Plan</h3>
                  <ProBadge />
                </div>
                <Settings2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-0.5">
                {[
                  ...(syllabus.topics[0]?.concepts ?? []).slice(0, 4),
                  ...(syllabus.topics[1]?.concepts ?? []).slice(0, 3),
                ].map((c, i) => (
                  <div key={i} className="w-full flex items-center gap-2.5 px-2 py-1.5">
                    <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-base flex-1 min-w-0 truncate">{c.name}</span>
                    <span className="text-sm text-muted-foreground shrink-0">→ Level 1</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lock overlay — in normal flow so parent sizes to content */}
          <div className="relative z-10 backdrop-blur-md bg-background/75 flex flex-col items-center px-6 pt-6 pb-5 text-center rounded-xl">
            {/* Lock icon + title */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                <Lock className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-center gap-1.5">
                  <p className="text-base font-semibold">Custom Study Plan</p>
                  <ProBadge />
                </div>
                <p className="text-xs text-muted-foreground max-w-[220px]">
                  A daily plan tailored to you
                </p>
              </div>
            </div>

            {/* Upgrade button */}
            <Link to="/upgrade" className={buttonVariants({ size: 'sm' }) + ' gap-1.5 mt-6 w-full max-w-xs'}>
              Upgrade
            </Link>
          </div>
        </div>
      )}
    </>
  )

  // What the Dashboard leads with, in this order:
  //
  //   1. **The exam at a glance** — one card: the readiness score inside a
  //      mini copy of the Study Guide ring, the days left and the exam date.
  //      The ring opens the full ring and its criteria in a popup
  //      (`studyGuideModal`).
  //   2. **The calendar** — the schedule strip (`examScheduleBlock`, with
  //      `dayDetailPanel` for whichever day is tapped), then the primary
  //      actions (`actions`) bare on the page.
  //   3. **Today's Study Plan** (`studyPlanCardContent`) — what to do about the
  //      score today.
  //
  // All of it portals into `readinessSlot` when the Dashboard supplies one.
  const readinessPct = readiness.counts.total > 0 ? Math.round(readiness.overallPct) : 0

  // The dates block inside that card. Two facts, in the order a candidate asks
  // for them: the day they are sitting — with how many days that leaves — and
  // then the next published sitting window, the second only when it says something
  // the first doesn't (no date chosen yet, or a date that falls outside every
  // known window for this exam). Nothing is inferred: the countdown is the
  // chosen date and the window is transcribed from `data/examSittings.ts`, so an
  // exam with no scheduled sitting simply offers the date picker.
  const examDateStep: 1 | 2 = (LOCALIZED_EXAMS[progressKey]?.length ?? 0) > 0 ? 2 : 1
  const nextSitting = useMemo(() => getSittingsForExam(progressKey)[0] ?? null, [progressKey])
  const daysToExam = examDate ? daysBetween(todayISO(), examDate) : null
  const showNextSitting = !!nextSitting && (!examDate || !isValidSittingDate(progressKey, examDate))
  // The sitting window the chosen date falls in, which the strip below shades
  // and runs a week past. Transcribed, like the next sitting: a date in no
  // known window shades nothing but exam day itself.
  const examWindow = useMemo(() => examWindowFor(progressKey, examDate), [progressKey, examDate])
  const countdownLabel = daysToExam === null ? null : daysLeftLabel(daysToExam)
  // The exam date, the countdown beside it, and — under both — the schedule
  // itself: the Study Schedule card's timeline, drawn linear so it spans exactly
  // the stretch the two lines above it name. The card used to sit further down
  // the dashboard saying the same thing twice; merged here, the date is the
  // deadline, the strip is what has been done about it, and tapping a day opens
  // that day below. Everything the separate card did — the day panel, the
  // schedule-forming sweep — happens in this block. The target-ready date has
  // no row of its own here: the strip marks its day in amber, and the Study
  // Plan modal is where it is set.
  const openExamDateStep = () => { setConfigInitialStep(examDateStep); setShowConfig(true) }
  const examScheduleBlock = (
    <div ref={studyScheduleCardRef} className="space-y-1">
      <div className="space-y-2">
        {/* The view toggle, with the schedule-forming status beside it while
            the sweep runs. */}
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 text-xs font-medium text-primary" aria-live="polite">
            {playback.active && (playback.landed && playback.summary
              ? `Schedule locked in — ${playback.summary.concepts} concept${playback.summary.concepts === 1 ? '' : 's'} across ${playback.summary.studyDays} study day${playback.summary.studyDays === 1 ? '' : 's'}`
              : 'Building your schedule…')}
          </p>
          <SegmentedControl
            label="Schedule view"
            size="xs"
            pill
            fit
            value={scheduleView}
            onChange={changeScheduleView}
            options={[
              { value: 'week', label: 'Week' },
              { value: 'overview', label: 'Overview' },
            ]}
            className="shrink-0"
          />
        </div>

        <ExamHeatmap
          layout="linear"
          linearView={scheduleView}
          showExamDateRow={false}
          sessions={examSessions}
          examProgressKey={progressKey}
          targetDate={examDate}
          onTargetDateChange={onExamDateChange ?? (() => {})}
          targetReadyDate={config.targetReadyDate}
          examWindow={examWindow}
          onOpenStudyPlan={(step) => { setConfigInitialStep(step ?? 1); setShowConfig(true) }}
          onDayClick={date => { playback.stop(); setSelectedDay(date) }}
          dayPlanPct={dayPlanPct}
          highlightedDay={displayDay}
          playbackDay={playback.day}
          playbackStepMs={playback.stepMs}
        />
      </div>
    </div>
  )

  // The day panel the strip above opens: whatever day was tapped (or, while a
  // locked-in plan is playing back, the day the sweep is on) — its sessions,
  // its gems and level-ups, and what the plan has scheduled for it.
  const dayDetailPanel = (
    <>
    {/* Day panel — shown when a heatmap day is clicked, and driven by the
        sweep while a locked-in schedule is playing back */}
    {displayDay && (() => {
      const daySessions = examSessions.filter(s => {
        const d = new Date(s.completed_at)
        const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        return localDate === displayDay
      })
      const dayTotal = daySessions.reduce((s, r) => s + r.total_questions, 0)
      const dayCorrect = daySessions.reduce((s, r) => s + r.correct_count, 0)
      const dayLevelUps = selectedDayLevelUps.length
      const dayLabel = new Date(displayDay + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
      const isFutureDay = displayDay > todayStr
      return (
        <div className="pt-3 space-y-4">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{dayLabel}</span>
            <button
              type="button"
              onClick={() => { playback.stop(); setSelectedDay(null) }}
              className="flex items-center justify-center h-8 w-8 rounded-full border border-border bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear day filter"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Large stats */}
          {daySessions.length > 0 && (
            <div className="flex items-center gap-5 flex-wrap">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold tabular-nums leading-none">
                  {dayCorrect}
                  <span className="text-muted-foreground text-lg font-normal">/{dayTotal}</span>
                </span>
                <span className="text-[10px] text-muted-foreground mt-0.5">correct</span>
              </div>
              {dayCorrect > 0 && (
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold tabular-nums leading-none text-cyan-500 inline-flex items-center gap-1">
                    {dayCorrect} <Gem className="h-5 w-5" />
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">gems</span>
                </div>
              )}
              {dayLevelUps > 0 && (
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold tabular-nums leading-none text-primary inline-flex items-center gap-1">
                    {dayLevelUps} <ArrowUp className="h-5 w-5" />
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">levelled up</span>
                </div>
              )}
            </div>
          )}

          {/* Session flashcard grid — a day with no sessions simply shows
              none: the day's plan below is the useful half, and during the
              schedule sweep an empty-state line would flicker on every day. */}
          {daySessions.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {daySessions.map(session => (
                <QuizSessionCard
                  key={session.id}
                  session={session}
                  onClick={() => {
                    savedScrollY.current = window.scrollY
                    setViewingSession(session)
                  }}
                />
              ))}
            </div>
          )}

          {/* Study plan for this day */}
          {isPro && (() => {
            // Today's plan lives in its own card, so clicking today's cell
            // normally shows nothing here — except during the sweep, where
            // landing on today is the whole point.
            if (displayDay === todayStr && !playback.active) return null
            if ((isFutureDay || playback.active) && plan) {
              const dayConcepts = plan.assignments.filter(a => a.scheduledDate === displayDay)
              // Capped and height-reserved during the sweep so a busy day
              // followed by a quiet one doesn't pump the whole dashboard.
              const futureConcepts = playback.active ? dayConcepts.slice(0, 4) : dayConcepts
              return (
                <div className={`border-t pt-3 space-y-1${playback.active ? ' min-h-[152px]' : ''}`}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Planned for this day</p>
                  {futureConcepts.length === 0 && (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">Nothing scheduled — review and rest.</p>
                  )}
                  {futureConcepts.map((a, i) => {
                    const cIdx = allConcepts.findIndex(c => c.name.toLowerCase() === a.conceptName.toLowerCase())
                    return (
                      <div
                        key={a.conceptName}
                        // The sweep passes a day in well under the time an
                        // entry animation takes, so mid-sweep the rows just
                        // swap — the list reads as scrubbing through the
                        // schedule. Only the day it lands on flashes in.
                        style={playback.landed ? { animationDelay: `${i * 40}ms` } : undefined}
                        className={`flex items-center gap-2.5 px-2 py-1.5${playback.landed ? ' schedule-playback-concept' : ''}`}
                      >
                        <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                        <button
                          type="button"
                          onClick={() => openDashboard(toRefs(allConcepts), null, 'entire-syllabus', cIdx === -1 ? 0 : cIdx)}
                          className="text-sm flex-1 min-w-0 truncate text-left hover:text-foreground/70 transition-colors"
                        >
                          {a.conceptName}
                        </button>
                        <span className="text-xs text-muted-foreground shrink-0">→ {MASTERY_LABEL[NEXT_STATE[a.initialState] ?? a.initialState]}</span>
                      </div>
                    )
                  })}
                  {playback.active && dayConcepts.length > futureConcepts.length && (
                    <p className="px-2 text-xs text-muted-foreground">
                      +{dayConcepts.length - futureConcepts.length} more
                    </p>
                  )}
                </div>
              )
            }
            // Past day: show level-ups
            if (selectedDayLevelUps.length > 0) {
              return (
                <div className="border-t pt-3 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Concepts mastered</p>
                  {selectedDayLevelUps.map(lu => {
                    const cIdx = allConcepts.findIndex(c => c.name.toLowerCase() === lu.conceptSlug.toLowerCase())
                    return (
                      <div key={lu.conceptSlug + lu.at} className="flex items-center gap-2.5 px-2 py-1.5">
                        <CheckMark className="h-4 w-4" />
                        <button
                          type="button"
                          onClick={() => openDashboard(toRefs(allConcepts), null, 'entire-syllabus', cIdx === -1 ? 0 : cIdx)}
                          className="text-sm flex-1 min-w-0 truncate text-left text-muted-foreground line-through hover:line-through hover:text-foreground/70 transition-colors"
                        >
                          {lu.conceptSlug}
                        </button>
                        <span className="text-xs text-green-600 dark:text-green-400 shrink-0 font-medium">→ {MASTERY_LABEL[lu.to]}</span>
                      </div>
                    )
                  })}
                </div>
              )
            }
            return null
          })()}

          {/* Empty state — every square on the strip opens this panel, so a
              day with nothing on it has to say so rather than render an
              empty box. Suppressed during the sweep, which passes days too
              fast for a message to be read. */}
          {!playback.active && daySessions.length === 0 && (() => {
            if (isFutureDay) {
              // A future day with a plan already shows "Planned for this day".
              if (isPro && plan) return null
              return <p className="text-sm text-muted-foreground">This day is still ahead — nothing recorded yet.</p>
            }
            if (displayDay === todayStr) {
              return <p className="text-sm text-muted-foreground">No quizzes finished yet today.</p>
            }
            if (selectedDayLevelUps.length > 0) return null
            return <p className="text-sm text-muted-foreground">No study activity on this day.</p>
          })()}
        </div>
      )
    })()}
    </>
  )

  const examDay = examDate ? new Date(examDate + 'T00:00:00') : null
  const accent = examAccentStyle(progressKey)

  const readinessCardContent = (
    <div className="order-none flex flex-col gap-6">
      {/* The exam at a glance — one card, in the exam's own colour, holding the
          three numbers the dashboard is about: how ready the reader is (the
          ring, which opens the Study Guide — `ringOpen`), how far off the exam
          is, and the day it falls on (both open the Study Plan modal on its
          exam-date step). They used to be three equal squares; the countdown
          and the date are one fact read two ways, and the score is the one the
          rest of the page acts on, so it leads and the two dates share a line. */}
      <div style={accent} className="relative overflow-hidden rounded-2xl bg-card text-card-foreground shadow-sm">
        {accent && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_0%_0%,var(--exam-accent-soft),transparent_65%)]"
          />
        )}
        <div className="relative flex items-center gap-4 p-4 sm:gap-8 sm:p-6">
          <button
            ref={readinessSquareRef}
            type="button"
            onClick={() => setRingOpen(true)}
            className="relative size-28 shrink-0 rounded-full transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:size-40"
            aria-haspopup="dialog"
            aria-label={`Exam readiness: ${readinessPct}% — open the study guide`}
          >
            <MiniReadinessRing segments={ringSegments} pct={readinessPct} flash={flashRadial} />
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="min-w-0 truncate text-sm font-medium text-muted-foreground">
                <span className="text-foreground">{syllabus.examLabel}</span>
                {syllabus.examTopic && <> · {syllabus.examTopic}</>}
              </p>
              {/* What today did to the score — green up, red down, absent when
                  it hasn't moved (docs/exam-readiness.md). */}
              {readinessMoveToday !== 0 && (
                <span
                  className={
                    'ml-auto flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-base font-semibold leading-none tabular-nums sm:px-3 sm:py-1.5 sm:text-xl ' +
                    (readinessMoveToday > 0
                      ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                      : 'bg-red-500/10 text-red-600 dark:text-red-400')
                  }
                  title={`Readiness ${readinessMoveToday > 0 ? 'up' : 'down'} ${Math.abs(readinessMoveToday)} point${Math.abs(readinessMoveToday) === 1 ? '' : 's'} today`}
                >
                  {readinessMoveToday > 0
                    ? <ArrowUp className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                    : <ArrowDown className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />}
                  {Math.abs(readinessMoveToday)}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={openExamDateStep}
              className="group mt-2 block max-w-full rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={
                examDate
                  ? `${countdownLabel ? countdownLabel + ', ' : ''}exam date ${formatReadableDate(examDate)} — change it`
                  : 'Set your exam date'
              }
            >
              {daysToExam !== null && daysToExam > 1 ? (
                <span className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold leading-none tracking-tight tabular-nums sm:text-5xl">{daysToExam}</span>
                  <span className="text-base text-muted-foreground">days left</span>
                </span>
              ) : (
                <span className={'block text-2xl font-bold leading-tight sm:text-3xl ' + (daysToExam === null || daysToExam < 0 ? 'text-muted-foreground' : '')}>
                  {countdownLabel ?? 'Set exam date'}
                </span>
              )}
              <span className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors group-hover:text-foreground">
                <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate tabular-nums">
                  {examDay
                    ? examDay.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Choose the day you sit it'}
                </span>
                <Pencil className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </span>
            </button>

            {/* The next published sitting, only when it says something the
                date doesn't: no date chosen yet, or one outside every known window. */}
            {showNextSitting && nextSitting && (
              <button
                type="button"
                onClick={openExamDateStep}
                className="mt-1.5 block max-w-full truncate text-left text-xs text-muted-foreground hover:text-foreground"
              >
                Next sitting · {formatSittingDate(nextSitting)}
                {nextSitting.format && <> · <span className="font-medium">{nextSitting.format}</span></>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* The calendar: the schedule strip, and the day it opens below it. */}
      <section aria-label="Study schedule" className="space-y-1">
        {examScheduleBlock}
        {dayDetailPanel}
      </section>

      {/* The ways to act on the score, bare on the page — no card around them. */}
      {actions}

      {studyPlanCardContent}
    </div>
  )

  // The Study Guide — the ring (one arc per syllabus concept) beside the
  // criterion the score is made of — in a popup the readiness square opens.
  // It sits *under* the concept popup (z-40) and ends where that pane begins
  // (`--concept-split-height`), so tapping an arc opens the concept below the
  // ring and the arc stays highlighted while the reader walks the syllabus.
  const studyGuideModal = ringOpen && (
    <OverlayPortal>
      <div
        className="fixed inset-x-0 top-0 z-[38] flex items-start justify-center overflow-y-auto bg-background/80 p-4 pt-16 backdrop-blur-sm paper-scrim sm:items-center lg:left-[var(--sidebar-width,16rem)] lg:pt-4"
        style={{ bottom: 'var(--concept-split-height, 0px)' }}
        onClick={() => setRingOpen(false)}
      >
        <div
          ref={studyGuideCardRef}
          role="dialog"
          aria-modal="true"
          aria-label="Study Guide"
          className="relative w-full max-w-2xl rounded-xl bg-card p-6 text-card-foreground shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="mb-4 flex items-center gap-3">
            <ExamLogo examKey={progressKey} size="md" />
            <h3 className="min-w-0 flex-1 text-lg font-semibold">Study Guide</h3>
            <button
              type="button"
              onClick={() => setRingOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
            <div className="w-full max-w-[280px] shrink-0 sm:w-[250px]">
              <StudyGuideRadial
                syllabus={syllabus}
                examRecords={examRecords}
                now={now}
                pct={readinessPct}
                selectedConcept={popupFromRadial ? popupCurrentName : null}
                flashRadial={flashRadial}
                onConceptClick={name => {
                  const idx = allConcepts.findIndex(c => c.name.toLowerCase() === name.toLowerCase())
                  openDashboard(toRefs(allConcepts), null, 'entire-syllabus', idx === -1 ? 0 : idx, { circular: true, fromRadial: true })
                }}
              />
            </div>

            <div className="w-full min-w-0 flex-1">
              {/* The criteria. Each bar's *thickness* is the weight it carries in
                  the headline score, so a heavier one would be visibly the
                  heavier line and nothing has to print "60% of score"
                  (docs/visual-noise-review.md §3.1). */}
              <div className="space-y-3">
                {readiness.criteria.map(criterion => {
                  const pct = Math.round(criterion.pct)
                  return (
                    <div
                      key={criterion.id}
                      className="space-y-1.5"
                      role="group"
                      aria-label={`${criterion.label}: ${pct}%, ${Math.round(criterion.weight * 100)}% of the readiness score`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium">{criterion.label}</span>
                        <span className="text-sm tabular-nums text-muted-foreground">{pct}%</span>
                      </div>
                      <div
                        className="w-full rounded-full bg-secondary overflow-hidden"
                        style={{ height: 4 + 6 * criterion.weight }}
                      >
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${criterion.pct}%`, backgroundColor: LEVEL3_TEXT }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* The sections syllabus coverage is made of, in syllabus order —
                  the same sections the ring's arcs are grouped into. */}
              {readiness.sections.some(s => s.total > 0) && (
                <ul className="mt-4 space-y-2.5 border-l border-border pl-3" aria-label="Coverage by section">
                  {readiness.sections.filter(s => s.total > 0).map(section => {
                    const pct = Math.round(section.readinessPct)
                    return (
                      <li key={section.name} className="space-y-1" aria-label={`${section.name}: ${pct}%`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="min-w-0 truncate text-xs text-muted-foreground" title={section.name}>
                            {section.name}
                          </span>
                          <span className="text-xs tabular-nums text-muted-foreground">{pct}%</span>
                        </div>
                        <div className="h-1 w-full overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${section.readinessPct}%`, backgroundColor: LEVEL3_TEXT }}
                          />
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </OverlayPortal>
  )

  return (
    <div className="space-y-4">
      {/* One column. The cards the Dashboard wants at the very top of the
          page — the schedule strip, Exam readiness and its actions,
          Today's Study Plan and the Study Guide — portal into the slot it
          supplies; what's left here is the plan's warnings, full width. The
          `order-*` classes keep the inline (no-slot) fallback in the same
          reading order: the readiness group, then warnings. */}
      <div className="flex flex-col gap-4">
      {/* Exam readiness + Today's Study Plan + Study Guide — portal to
          `readinessSlot` when the Dashboard supplies one, otherwise render inline
          here (`order-none`, so they stay first). */}
      {readinessSlot ? createPortal(readinessCardContent, readinessSlot) : readinessCardContent}
      {studyGuideModal}

      {/* Warnings — `order-3` sits them below the primary actions; `empty:hidden`
          drops the flex gap when the note isn't shown. */}
      <div className="order-3 flex flex-col gap-4 empty:hidden">
        {!loading && plan?.status === 'review_mode' && (
          <ReviewModeNote concepts={plan.reviewConcepts ?? []} />
        )}
      </div>
      </div>

      {showConfig && (
        <StudyPlanConfigModal
          config={config}
          examDate={examDate}
          examLabel={syllabus.examLabel}
          examId={wikiExamIdToProgressKey(syllabus.examId)}
          initialStep={configInitialStep}
          isPro={isPro}
          onSave={next => {
            onConfigChange(next)
            onRegenerate()
          }}
          onExamDateChange={onExamDateChange}
          onClose={() => setShowConfig(false)}
        />
      )}
      <DayCompleteInfoPanel
        open={showDayCompleteInfo}
        onClose={() => setShowDayCompleteInfo(false)}
        conceptsCompleted={examCompletedToday.map(lu => ({ name: lu.conceptSlug, label: MASTERY_LABEL[lu.to] }))}
        questionsAnswered={todayQuestionsAnswered}
        gemsEarned={todayGemsEarned}
        bonusClaimed={bonusClaimed}
        bonusAmount={claimedBonusAmount}
      />

      {/* Session completion overlay */}
      {viewingSession && (
        <SessionCompletionOverlay
          session={viewingSession}
          isLoggedIn={!!user}
          onClose={() => {
            setViewingSession(null)
            requestAnimationFrame(() => window.scrollTo({ top: savedScrollY.current, behavior: 'instant' }))
          }}
        />
      )}
    </div>
  )
}

// ── Day Complete Info Panel ───────────────────────────────────────────────────

function DayCompleteInfoPanel({
  open, onClose, conceptsCompleted, questionsAnswered, gemsEarned, bonusClaimed, bonusAmount,
}: {
  open: boolean
  onClose: () => void
  conceptsCompleted: { name: string; label: string }[]
  questionsAnswered: number
  gemsEarned: number
  bonusClaimed: boolean
  bonusAmount: number
}) {
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto paper-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Today's study plan complete"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-sm bg-card border rounded-xl shadow-2xl flex flex-col my-16">
        <div className="flex items-center gap-2 px-4 h-12 border-b shrink-0">
          <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
          <span className="flex-1 font-semibold text-sm">Today's Study Plan Complete</span>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1 transition-colors"
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm leading-relaxed">
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2.5 flex items-center gap-2.5">
            <Gem className="h-5 w-5 text-cyan-400 shrink-0" />
            <p className="text-cyan-700 dark:text-cyan-300">
              {bonusClaimed
                ? <><strong>2× gem bonus applied</strong> — +{bonusAmount} bonus gem{bonusAmount === 1 ? '' : 's'} earned today.</>
                : <>2× gem bonus is active for the rest of today.</>}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">Today's achievements</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>· Answered {questionsAnswered} question{questionsAnswered === 1 ? '' : 's'}, earning {gemsEarned} gem{gemsEarned === 1 ? '' : 's'}</li>
              <li>· Levelled up {conceptsCompleted.length} concept{conceptsCompleted.length === 1 ? '' : 's'}</li>
            </ul>
          </div>

          {conceptsCompleted.length > 0 && (
            <div className="rounded-lg border bg-muted/30 px-3 py-2.5 space-y-1 max-h-40 overflow-y-auto">
              {conceptsCompleted.map(c => (
                <div key={c.name} className="flex items-center gap-2 text-xs">
                  <CheckMark className="h-3.5 w-3.5" />
                  <span className="flex-1 min-w-0 truncate">{c.name}</span>
                  <span className="text-green-600 dark:text-green-400 font-medium shrink-0">→ {c.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 pb-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}
